import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ImproveStats {
  total: number;
  improved: number;
  generated: number;
  tags_changed: number;
  skipped: number;
  errors: number;
}

interface ImproveResult {
  dry_run: boolean;
  stats: ImproveStats;
  errors?: { id: string; name: string; error: string }[];
  previews?: { id: string; name: string; mode: string; words: number; tags: string[] }[];
}

/**
 * Hook to trigger the admin-improve-places batch job.
 * WHY: Allows admins to bulk-improve place content quality from the UI.
 */
export function useImprovePlaces() {
  const { toast } = useToast();
  const [lastResult, setLastResult] = useState<ImproveResult | null>(null);

  const mutation = useMutation({
    mutationFn: async ({ dryRun = false, limit = 0 }: { dryRun?: boolean; limit?: number } = {}) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const resp = await supabase.functions.invoke("admin-improve-places", {
        body: { dry_run: dryRun, limit },
      });

      if (resp.error) throw resp.error;
      return resp.data as ImproveResult;
    },
    onSuccess: (data) => {
      setLastResult(data);
      const { stats } = data;
      toast({
        title: data.dry_run ? "Dry run klar" : "Platser förbättrade",
        description: `${stats.generated} nya, ${stats.improved} förbättrade, ${stats.tags_changed} taggar ändrade, ${stats.skipped} överhoppade, ${stats.errors} fel`,
      });
    },
    onError: (err) => {
      toast({
        title: "Fel vid förbättring",
        description: String(err),
        variant: "destructive",
      });
    },
  });

  return {
    improve: mutation.mutate,
    isRunning: mutation.isPending,
    lastResult,
  };
}
